/**
 * ProductOnboardingProvider — shared cache for in-product onboarding checklist.
 *
 * Fetches once per session for self-serve tenants only; refreshes on step
 * completion, sync-job finish, or tab visibility — not on every route change.
 */
import React from 'react';
import { buildApiUrl } from '../config';
import { trackProductOnboardingStepCompleted } from '../utils/analytics';
import type {
  ProductOnboardingState,
  ProductStep,
  ProductStepId,
  ProductStepPayload,
} from '../hooks/useProductOnboardingTypes';

export type {
  ProductOnboardingState,
  ProductStep,
  ProductStepId,
  ProductStepPayload,
} from '../hooks/useProductOnboardingTypes';

const STEP_EVENT = 'klaris:product-step-complete';
const SYNC_JOB_FINISHED_EVENT = 'klaris:sync-job-finished';

function getTenantId(): string | null {
  try {
    const raw = localStorage.getItem('klaris_tenant');
    if (!raw) return null;
    return JSON.parse(raw).tenant_id ?? null;
  } catch {
    return null;
  }
}

function getToken(): string | null {
  try {
    return localStorage.getItem('klaris_jwt');
  } catch {
    return null;
  }
}

function isSelfServeTenant(): boolean {
  try {
    const raw = localStorage.getItem('klaris_tenant');
    if (!raw) return false;
    return JSON.parse(raw).onboarding_source === 'self_serve';
  } catch {
    return false;
  }
}

let globalResetProductOnboarding: (() => void) | null = null;

export function resetProductOnboardingOnLogout() {
  globalResetProductOnboarding?.();
}

export async function markProductStep(stepId: ProductStepId, data?: ProductStepPayload): Promise<void> {
  const tenantId = getTenantId();
  const token = getToken();
  if (!tenantId || !token) return;
  try {
    const resp = await fetch(
      buildApiUrl(`/api/v1/tenants/${tenantId}/product-onboarding/complete-step`),
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ step: stepId, data: data || undefined }),
      },
    );
    if (resp.ok) {
      trackProductOnboardingStepCompleted({
        step_id: stepId,
        ...(data?.dashboard_id ? { dashboard_id: data.dashboard_id } : {}),
      });
      window.dispatchEvent(new CustomEvent(STEP_EVENT, { detail: { step: stepId, data } }));
    }
  } catch {
    // Non-critical
  }
}

export async function dismissProductOnboarding(): Promise<boolean> {
  const tenantId = getTenantId();
  const token = getToken();
  if (!tenantId || !token) return false;
  try {
    const resp = await fetch(
      buildApiUrl(`/api/v1/tenants/${tenantId}/product-onboarding/dismiss`),
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      },
    );
    if (resp.ok) {
      window.dispatchEvent(new CustomEvent(STEP_EVENT, { detail: { dismissed: true } }));
      return true;
    }
  } catch {
    // Non-critical
  }
  return false;
}

interface ProductOnboardingContextValue {
  steps: ProductStep[];
  starterQuestions: string[];
  completedCount: number;
  totalCount: number;
  currentStepId: ProductStepId | null;
  isDismissed: boolean;
  isSelfServeProductOnboarding: boolean;
  shouldShow: boolean;
  loading: boolean;
  completeStep: (stepId: ProductStepId) => Promise<void>;
  dismiss: () => Promise<void>;
  refresh: () => Promise<void>;
}

const ProductOnboardingContext = React.createContext<ProductOnboardingContextValue>({
  steps: [],
  starterQuestions: [],
  completedCount: 0,
  totalCount: 4,
  currentStepId: null,
  isDismissed: false,
  isSelfServeProductOnboarding: false,
  shouldShow: false,
  loading: true,
  completeStep: async () => {},
  dismiss: async () => {},
  refresh: async () => {},
});

export function useProductOnboarding(): ProductOnboardingContextValue {
  return React.useContext(ProductOnboardingContext);
}

export default function ProductOnboardingProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<ProductOnboardingState | null>(null);
  const [loading, setLoading] = React.useState(() => isSelfServeTenant());
  const [tenantId, setTenantId] = React.useState<string | null>(getTenantId);
  const isEligible = isSelfServeTenant();

  React.useEffect(() => {
    const checkTenant = () => {
      const next = getTenantId();
      setTenantId((prev) => (prev === next ? prev : next));
    };
    checkTenant();
    const interval = setInterval(checkTenant, 200);
    return () => clearInterval(interval);
  }, []);

  const fetchState = React.useCallback(async (signal?: AbortSignal) => {
    const token = getToken();
    if (!tenantId || !token || !isSelfServeTenant()) {
      setState(null);
      setLoading(false);
      return;
    }
    try {
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/product-onboarding`), {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
        signal,
      } as RequestInit);
      if (signal?.aborted) return;
      if (resp.ok) {
        setState(await resp.json());
      }
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [tenantId]);

  // Initial session load (and tenant switch) — not on every client-side navigation.
  React.useEffect(() => {
    if (!isSelfServeTenant()) {
      setState(null);
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    fetchState(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchState]);

  React.useEffect(() => {
    const handler = () => {
      void fetchState();
    };
    window.addEventListener(STEP_EVENT, handler);
    return () => window.removeEventListener(STEP_EVENT, handler);
  }, [fetchState]);

  React.useEffect(() => {
    const handler = () => {
      void fetchState();
    };
    window.addEventListener(SYNC_JOB_FINISHED_EVENT, handler);
    return () => window.removeEventListener(SYNC_JOB_FINISHED_EVENT, handler);
  }, [fetchState]);

  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void fetchState();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchState]);

  React.useEffect(() => {
    globalResetProductOnboarding = () => {
      setState(null);
      setLoading(false);
    };
    return () => {
      globalResetProductOnboarding = null;
    };
  }, []);

  const completeStep = React.useCallback(async (stepId: ProductStepId) => {
    await markProductStep(stepId);
  }, []);

  const dismiss = React.useCallback(async () => {
    const tenantIdNow = getTenantId();
    const token = getToken();
    if (!tenantIdNow || !token) return;
    try {
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantIdNow}/product-onboarding/dismiss`),
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        },
      );
      if (resp.ok) {
        setState(await resp.json());
      }
    } catch {
      // non-critical
    }
  }, []);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    await fetchState();
  }, [fetchState]);

  const allDone = state ? state.completed_count === state.total_count : false;
  const shouldShow = isEligible && !loading && state !== null && !state.dismissed && !allDone;

  const value = React.useMemo<ProductOnboardingContextValue>(
    () => ({
      steps: state?.steps ?? [],
      starterQuestions: state?.starter_questions ?? [],
      completedCount: state?.completed_count ?? 0,
      totalCount: state?.total_count ?? 4,
      currentStepId: state?.current_step_id ?? null,
      isDismissed: state?.dismissed ?? false,
      isSelfServeProductOnboarding: isEligible,
      shouldShow,
      loading,
      completeStep,
      dismiss,
      refresh,
    }),
    [state, isEligible, shouldShow, loading, completeStep, dismiss, refresh],
  );

  return (
    <ProductOnboardingContext.Provider value={value}>{children}</ProductOnboardingContext.Provider>
  );
}
